/**
 * Provider List Page
 *
 * Displays all providers for the current tenant/vertical with search,
 * status filtering, pagination, and create/delete actions.
 * Capability-gated to users with products:read.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Search,
  Loader2,
  AlertCircle,
  RefreshCw,
  Package,
  Pencil,
  Trash2,
  ChevronRight,
  Home,
} from "lucide-react";
import type { ReactElement, ChangeEvent } from "react";
import { useState, useCallback, useMemo } from "react";
import { Link, Navigate, useParams, useNavigate } from "react-router-dom";

import { apiRequest } from "../../api/client";
import { DeleteConfirmationDialog } from "../../components/providers/DeleteConfirmationDialog";
import {
  ProviderCreateDialog,
  type ProviderCreateFormValues,
} from "../../components/providers/ProviderCreateDialog";
import { ProviderStatusBadge } from "../../components/providers/ProviderStatusBadge";
import { useAuthStore } from "../../store/authStore";

// ============================================================================
// Types
// ============================================================================

/** Provider response shape from API */
interface ProviderResponse {
  readonly id: string;
  readonly externalId: string;
  readonly name: string;
  readonly slug: string;
  readonly status: "active" | "inactive" | "archived" | "deleted";
  readonly metadata: {
    readonly logoUrl?: string;
    readonly websiteUrl?: string;
    readonly clickOutUrlTemplate?: string;
    readonly contactEmail?: string;
    readonly trackingParams?: Readonly<Record<string, string>>;
    readonly description?: string;
  };
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Provider list API response */
interface ProviderListApiResponse {
  readonly items: readonly ProviderResponse[];
  readonly pagination: {
    readonly total: number;
    readonly limit: number;
    readonly offset: number;
    readonly hasMore: boolean;
  };
  readonly timestamp: string;
}

/** List filter state */
interface ProviderListFilters {
  readonly search: string;
  readonly status: "all" | "active" | "inactive" | "archived";
  readonly limit: number;
  readonly offset: number;
}

// ============================================================================
// Status Tabs
// ============================================================================

const STATUS_TABS: ReadonlyArray<{ value: ProviderListFilters["status"]; label: string }> = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "archived", label: "Archived" },
];

// ============================================================================
// Main Component
// ============================================================================

export function ProviderListPage(): ReactElement {
  const { hasCapability } = useAuthStore();

  if (!hasCapability("products:read")) {
    return <Navigate to="/studio" replace />;
  }

  return <ProviderListContent />;
}

function ProviderListContent(): ReactElement {
  const { verticalKey } = useParams<{ verticalKey: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasCapability } = useAuthStore();

  const canWrite = hasCapability("products:write");
  const canDelete = hasCapability("products:delete");

  const [filters, setFilters] = useState<ProviderListFilters>({
    search: "",
    status: "all",
    limit: 20,
    offset: 0,
  });

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deletingProvider, setDeletingProvider] = useState<ProviderResponse | null>(null);

  // Debounced search for query key
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimerRef = useMemo(() => ({ current: 0 }), []);

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const value = e.target.value;
      setFilters((prev) => ({ ...prev, search: value, offset: 0 }));

      window.clearTimeout(searchTimerRef.current);
      searchTimerRef.current = window.setTimeout(() => {
        setDebouncedSearch(value);
      }, 300);
    },
    [searchTimerRef]
  );

  const handleStatusChange = useCallback((status: ProviderListFilters["status"]): void => {
    setFilters((prev) => ({ ...prev, status, offset: 0 }));
  }, []);

  // ==========================================================================
  // Data Fetching
  // ==========================================================================

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      "providers",
      verticalKey,
      filters.status,
      debouncedSearch,
      filters.limit,
      filters.offset,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("limit", String(filters.limit));
      params.set("offset", String(filters.offset));
      if (filters.status !== "all") params.set("status", filters.status);

      const query = params.toString() ? `?${params.toString()}` : "";
      const response = await apiRequest<ProviderListApiResponse>(`/api/v1/providers${query}`);

      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
  });

  // Client-side name filter (search is not supported by the API query params as a name filter)
  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    if (!debouncedSearch.trim()) return data.items;
    const term = debouncedSearch.toLowerCase();
    return data.items.filter(
      (p) => p.name.toLowerCase().includes(term) || p.slug.toLowerCase().includes(term)
    );
  }, [data?.items, debouncedSearch]);

  // ==========================================================================
  // Create Mutation
  // ==========================================================================

  const createMutation = useMutation({
    mutationFn: async (values: ProviderCreateFormValues) => {
      const metadata: Record<string, unknown> = {};
      if (values.description) metadata["description"] = values.description;
      if (values.logoUrl) metadata["logoUrl"] = values.logoUrl;
      if (values.websiteUrl) metadata["websiteUrl"] = values.websiteUrl;
      if (values.contactEmail) metadata["contactEmail"] = values.contactEmail;

      const body = {
        name: values.name,
        slug: values.slug,
        ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
      };

      const response = await apiRequest<ProviderResponse>(`/api/v1/providers`, {
        method: "POST",
        body,
      });

      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: (provider) => {
      queryClient.invalidateQueries({ queryKey: ["providers"] });
      setCreateDialogOpen(false);
      navigate(`/studio/${verticalKey}/providers/${provider.externalId}`);
    },
  });

  // ==========================================================================
  // Delete Mutation
  // ==========================================================================

  const deleteMutation = useMutation({
    mutationFn: async (providerId: string) => {
      const response = await apiRequest<unknown>(`/api/v1/providers/${providerId}`, {
        method: "DELETE",
      });
      if (!response.success) {
        throw new Error(response.error.message);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["providers"] });
      setDeletingProvider(null);
    },
  });

  const handleCreateSubmit = useCallback(
    (values: ProviderCreateFormValues): void => {
      createMutation.mutate(values);
    },
    [createMutation]
  );

  const handleDeleteConfirm = useCallback((): void => {
    if (!deletingProvider) return;
    deleteMutation.mutate(deletingProvider.externalId);
  }, [deletingProvider, deleteMutation]);

  const handlePrevPage = useCallback((): void => {
    setFilters((prev) => ({
      ...prev,
      offset: Math.max(0, prev.offset - prev.limit),
    }));
  }, []);

  const handleNextPage = useCallback((): void => {
    setFilters((prev) => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  }, []);

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <div className="flex h-full flex-col">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 border-b border-border bg-background-secondary px-4 py-2 text-sm">
        <Link
          to="/studio"
          className="flex items-center gap-1 text-foreground-muted hover:text-foreground"
        >
          <Home className="h-4 w-4" />
          <span>Studio</span>
        </Link>
        <ChevronRight className="h-4 w-4 text-foreground-muted" />
        <Link to={`/studio/${verticalKey}`} className="text-foreground-muted hover:text-foreground">
          {verticalKey}
        </Link>
        <ChevronRight className="h-4 w-4 text-foreground-muted" />
        <span className="font-medium">Providers</span>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {/* Page header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Providers</h1>
            <p className="mt-1 text-foreground-muted">Manage product providers for this vertical</p>
          </div>
          {canWrite && (
            <button onClick={() => setCreateDialogOpen(true)} className="btn btn-primary">
              <Plus className="mr-2 h-4 w-4" />
              Add Provider
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="mb-6 space-y-4">
          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-muted" />
            <input
              type="text"
              placeholder="Search providers..."
              value={filters.search}
              onChange={handleSearchChange}
              className="w-full rounded-md border border-border bg-background py-2 pl-10 pr-4 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Status tabs */}
          <div className="flex gap-1 border-b border-border">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => handleStatusChange(tab.value)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  filters.status === tab.value
                    ? "border-primary text-primary"
                    : "border-transparent text-foreground-muted hover:text-foreground hover:border-foreground-muted/30"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState
            message={error instanceof Error ? error.message : "Failed to load providers"}
            onRetry={refetch}
          />
        ) : filteredItems.length === 0 ? (
          <EmptyState
            hasFilters={Boolean(filters.search || filters.status !== "all")}
            {...(canWrite ? { onCreateClick: () => setCreateDialogOpen(true) } : {})}
          />
        ) : (
          <>
            {/* Provider table */}
            <div className="rounded-lg border border-border bg-background-secondary overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-background">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                      Provider
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                      Slug
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                      Created
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredItems.map((provider) => (
                    <ProviderRow
                      key={provider.externalId}
                      provider={provider}
                      verticalKey={verticalKey ?? ""}
                      canDelete={canDelete}
                      onDelete={setDeletingProvider}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data?.pagination && data.pagination.total > filters.limit && (
              <PaginationControls
                offset={filters.offset}
                limit={filters.limit}
                total={data.pagination.total}
                hasMore={data.pagination.hasMore}
                onPrev={handlePrevPage}
                onNext={handleNextPage}
              />
            )}
          </>
        )}
      </div>

      {/* Create Dialog */}
      <ProviderCreateDialog
        isOpen={createDialogOpen}
        isSubmitting={createMutation.isPending}
        {...(createMutation.error instanceof Error ? { error: createMutation.error.message } : {})}
        onSubmit={handleCreateSubmit}
        onClose={() => {
          setCreateDialogOpen(false);
          createMutation.reset();
        }}
      />

      {/* Delete Dialog */}
      {deletingProvider && (
        <DeleteConfirmationDialog
          providerName={deletingProvider.name}
          isLoading={deleteMutation.isPending}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeletingProvider(null)}
        />
      )}
    </div>
  );
}

// ============================================================================
// Provider Row
// ============================================================================

interface ProviderRowProps {
  readonly provider: ProviderResponse;
  readonly verticalKey: string;
  readonly canDelete: boolean;
  readonly onDelete: (provider: ProviderResponse) => void;
}

function ProviderRow({
  provider,
  verticalKey,
  canDelete,
  onDelete,
}: ProviderRowProps): ReactElement {
  return (
    <tr className="group hover:bg-background transition-colors">
      <td className="px-4 py-3">
        <Link
          to={`/studio/${verticalKey}/providers/${provider.externalId}`}
          className="flex items-center gap-3"
        >
          {provider.metadata.logoUrl ? (
            <img
              src={provider.metadata.logoUrl}
              alt={`${provider.name} logo`}
              className="h-8 w-8 rounded object-contain bg-background border border-border"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded bg-primary/10 text-primary">
              <Package className="h-4 w-4" />
            </div>
          )}
          <span className="font-medium text-foreground group-hover:text-primary transition-colors">
            {provider.name}
          </span>
        </Link>
      </td>
      <td className="px-4 py-3">
        <span className="font-mono text-sm text-foreground-muted">{provider.slug}</span>
      </td>
      <td className="px-4 py-3">
        <ProviderStatusBadge status={provider.status} />
      </td>
      <td className="px-4 py-3 text-sm text-foreground-muted">
        {new Date(provider.createdAt).toLocaleDateString()}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/studio/${verticalKey}/providers/${provider.externalId}`}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-foreground-muted hover:bg-background hover:text-foreground"
            title="Edit provider"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Link>
          {canDelete && (
            <button
              onClick={() => onDelete(provider)}
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-error hover:bg-error/10"
              title="Delete provider"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ============================================================================
// Pagination Controls
// ============================================================================

interface PaginationControlsProps {
  readonly offset: number;
  readonly limit: number;
  readonly total: number;
  readonly hasMore: boolean;
  readonly onPrev: () => void;
  readonly onNext: () => void;
}

function PaginationControls({
  offset,
  limit,
  total,
  hasMore,
  onPrev,
  onNext,
}: PaginationControlsProps): ReactElement {
  const start = offset + 1;
  const end = Math.min(offset + limit, total);
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="mt-4 flex items-center justify-between">
      <p className="text-sm text-foreground-muted">
        Showing {start} to {end} of {total} providers
      </p>
      <div className="flex items-center gap-2">
        <button
          onClick={onPrev}
          disabled={offset <= 0}
          className="btn btn-secondary px-3 py-1.5 text-sm disabled:opacity-50"
        >
          Previous
        </button>
        <span className="text-sm text-foreground-muted">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={onNext}
          disabled={!hasMore}
          className="btn btn-secondary px-3 py-1.5 text-sm disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// State Components
// ============================================================================

function LoadingState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
      <Loader2 className="h-8 w-8 animate-spin" />
      <p className="mt-4">Loading providers...</p>
    </div>
  );
}

interface ErrorStateProps {
  readonly message: string;
  readonly onRetry: () => void;
}

function ErrorState({ message, onRetry }: ErrorStateProps): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
        <AlertCircle className="h-6 w-6 text-error" />
      </div>
      <p className="mt-4 text-foreground-muted">{message}</p>
      <button onClick={onRetry} className="btn btn-secondary mt-4">
        <RefreshCw className="mr-2 h-4 w-4" />
        Try again
      </button>
    </div>
  );
}

interface EmptyStateProps {
  readonly hasFilters: boolean;
  readonly onCreateClick?: () => void;
}

function EmptyState({ hasFilters, onCreateClick }: EmptyStateProps): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-background-secondary">
        <Package className="h-8 w-8 text-foreground-muted" />
      </div>
      <h3 className="mt-4 font-semibold">
        {hasFilters ? "No providers match your filters" : "No providers found"}
      </h3>
      <p className="mt-2 text-foreground-muted">
        {hasFilters
          ? "Try adjusting your search or filters"
          : "Create your first provider to get started."}
      </p>
      {!hasFilters && onCreateClick && (
        <button onClick={onCreateClick} className="btn btn-primary mt-4">
          <Plus className="mr-2 h-4 w-4" />
          Add Provider
        </button>
      )}
    </div>
  );
}
